import { useCallback, useState } from "react";
import { EnableTotpRepositoryImpl } from "../../infrastructure/repositories/EnableTotpRepositoryImpl";
import { SessionManager } from "../../infrastructure/session/SessionManager";
import { getBearerTokenForEmailOtp } from "../../infrastructure/session/getBearerTokenForEmailOtp";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";
import {
  OTP_PREFERENCE_TOTP,
  PENDING_OTP_PREFERENCE_KEY,
} from "../../domain/constants/otpConstants";
import { patchOtpPreference } from "@/features/user-settings/infrastructure/api/userSettingsApi";
import type { AllTokens } from "../../domain/entities/AllTokens";
import type { EnableTotp } from "../../domain/entities/EnableTotp";

export const useEnableTotp = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyTotpSetup = useCallback(async (totp_code: string): Promise<EnableTotp> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new EnableTotpRepositoryImpl();
      const sessionPort = new SessionManager();
      const flowPurpose = sessionStorage.getItem(AUTH_FLOW_PURPOSE_KEY);
      const accessToken =
        flowPurpose === AUTH_FLOW_PURPOSE_RESET_TOTP
          ? sessionPort.getAccessToken()
          : sessionPort.getTempAccessToken() || sessionPort.getAccessToken();

      if (!accessToken) {
        throw new Error("No access token available");
      }

      const result = await repo.verifyTotpSetup(accessToken, totp_code);

      sessionPort.saveSession({
        ...{
          id: "",
          name: "",
          email: result.email,
          phone: "",
          institute_id: "",
          is_active: false,
          access_token: sessionPort.getAccessToken() ?? accessToken,
          refresh_token: sessionPort.getRefreshToken() ?? undefined,
          temp_access_token: sessionPort.getTempAccessToken() ?? undefined,
          is_totp_enabled: true,
          is_password_set: sessionPort.getIsPasswordSet() ?? false,
        } as Parameters<typeof sessionPort.saveSession>[0],
      });

      if (flowPurpose === AUTH_FLOW_PURPOSE_RESET_TOTP) {
        sessionStorage.removeItem(AUTH_FLOW_PURPOSE_KEY);
        sessionStorage.removeItem("temp_access_token");
      }

      const pendingPreference = sessionStorage.getItem(PENDING_OTP_PREFERENCE_KEY);
      if (pendingPreference === OTP_PREFERENCE_TOTP) {
        sessionStorage.removeItem(PENDING_OTP_PREFERENCE_KEY);
        const token = sessionPort.getAccessToken() || accessToken;
        if (token) {
          await patchOtpPreference(token, OTP_PREFERENCE_TOTP);
        }
      }

      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyTotp = useCallback(async (
    email: string,
    totp_code: string,
    purpose: string,
  ): Promise<AllTokens> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new EnableTotpRepositoryImpl();
      const sessionPort = new SessionManager();
      const tempAccessToken = sessionPort.getTempAccessToken();
      if (!tempAccessToken) {
        throw new Error("No temp access token available");
      }
      const result = await repo.verifyTotp(email, totp_code, purpose, tempAccessToken);

      sessionPort.saveTokens(result);

      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetTotp = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new EnableTotpRepositoryImpl();
      const sessionPort = new SessionManager();
      const tempToken = sessionPort.getTempAccessToken();
      if (!tempToken) {
        throw new Error("No temporary access token available");
      }
      await repo.resetTotp(tempToken);
      sessionStorage.removeItem("temp_access_token");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (
    email: string,
    otp_code: string,
    purpose: string,
  ): Promise<AllTokens> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new EnableTotpRepositoryImpl();
      const sessionPort = new SessionManager();
      const bearerToken = getBearerTokenForEmailOtp(purpose, sessionPort);
      const result = await repo.verifyOtp(
        email,
        otp_code,
        purpose,
        bearerToken,
      );

      if (purpose === AUTH_FLOW_PURPOSE_RESET_TOTP) {
        const tempToken =
          result.temp_access_token || result.access_token || "";
        if (!tempToken) {
          throw new Error("No temporary access token received");
        }
        sessionPort.saveTempAccessToken(tempToken);
      } else {
        sessionPort.saveTokens({
          access_token: result.access_token,
          refresh_token: result.refresh_token,
          temp_access_token: result.temp_access_token || result.access_token,
        });
      }

      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { verifyTotpSetup, verifyTotp, verifyOtp, resetTotp, loading, error };
};
